import React, { useState, useEffect, useCallback } from "react";
import "./Resellers.css";

const getLogoHeight = (logo) => {
  if (logo.icon.includes('UAV_Gaurage.svg')) return '70px';
  if (logo.icon.includes('Evelta.svg')) return '90px';
  return '50px';
};

const Resellers = () => {
  const [resellersData, setResellersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleImageError = useCallback((e) => {
    if (e.target.dataset.errorHandled === 'true') return;
    e.target.dataset.errorHandled = 'true';
    e.target.style.display = "none"; // hide broken images
  }, []);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchResellers = async () => {
      try {
        setError(null);

        const response = await fetch(`${process.env.REACT_APP_CMS_BASE_URL}/api/resellers`);

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();

        if (!isMounted) return;

        if (data.success && data.product_types) {
          const transformedData = data.product_types
            .filter(product => product.store && product.store.length > 0)
            .map(product => ({
              id: product.id || product.name?.toLowerCase().replace(/\s+/g, '-'),
              category: product.title || product.name,
              image: product.banner_image
                ? `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${product.banner_image}`
                : null,
              logos: product.store.map(store => ({
                id: store.id || store.title?.toLowerCase().replace(/\s+/g, '-'),
                title: store.title,
                icon: store.icon
                  ? `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${store.icon}`
                  : null,
                url: store.url
              }))
            }));

          setResellersData(transformedData);
        }
      } catch (err) {
        if (isMounted && err.name !== 'AbortError') {
          console.error('API Error:', err);
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchResellers();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  return (
    <div className="resellers-container">
      <h2 className="resellers-title">Resellers</h2>

      {loading && <div className="loading-indicator">Loading...</div>}
      {error && <div className="error-indicator">Failed to load: {error}</div>}

      {!loading && resellersData.length === 0 && (
        <div className="empty-message">No resellers available.</div>
      )}

      {resellersData.map((item) => (
        <div key={item.id} className="reseller-block">
          {item.image && (
            <img
              src={item.image}
              alt={item.category}
              className="reseller-category-img"
              onError={handleImageError}
              loading="lazy"
            />
          )}
          <h3 className="reseller-category">{item.category}</h3>
          <div className="reseller-logos">
            {item.logos.map((logo) => (
              logo.icon && (
                <div className="logo-box" key={logo.id}>
                  {logo.url ? (
                    <a
                      href={logo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Visit ${logo.title}`}
                    >
                      <img
                        src={logo.icon}
                        alt={logo.title}
                        className="logo-img"
                        title={logo.title}
                        onError={handleImageError}
                        loading="lazy"
                        style={{ height: getLogoHeight(logo), width: '175px' }}
                      />
                    </a>
                  ) : (
                    <img
                      src={logo.icon}
                      alt={logo.title}
                      className="logo-img"
                      onError={handleImageError}
                      loading="lazy"
                      style={{ height: getLogoHeight(logo), width: '175px' }}
                    />
                  )}
                </div>
              )
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Resellers;
